import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Tabs,
  Tab,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import datetimeDecorator from 'src/decorators/datetime.decorator';

type DriverDailyCost = {
  deliveryId: number;
  driverId: number;
  truckId: number;
  truckName: string;
  truckKlass: string;
  date: string; // YYYY-MM-DD形式
  workingHours: number; // 時間単位
  dayStartAt: Date;
  dayEndAt: Date;
  cost: number;
  sales: number;
};

type DailyTruckSummary = {
  date: string;
  truckId: number;
  truckName: string;
  truckKlass: string;
  totalCost: number;
  totalSales: number;
  totalProfit: number;
  totalWorkingHours: number;
};

type DailyKlassSummary = {
  date: string;
  truckKlass: string;
  totalCost: number;
  totalSales: number;
  totalProfit: number;
  totalWorkingHours: number;
  truckCount: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  driverDailyCosts: DriverDailyCost[];
  startDate: Date;
  endDate: Date;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = memo(({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`cost-analysis-tabpanel-${index}`}
    aria-labelledby={`cost-analysis-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    )}
  </div>
));

const CostAnalysisDialog: FC<Props> = memo(({
  open,
  onClose,
  driverDailyCosts,
  startDate,
  endDate
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [filteredDriverDailyCosts, setFilteredDriverDailyCosts] = useState<DriverDailyCost[]>([]);

  useEffect(() => {
    const filtered = driverDailyCosts.filter((cost) => {
      if (datetimeDecorator.toYyyyMmDd(startDate, '/') <= cost.date && cost.date <= datetimeDecorator.toYyyyMmDd(endDate, '/')) {
        return true;
      }
      return false;
    });
    setFilteredDriverDailyCosts(filtered);
  }, [driverDailyCosts, startDate, endDate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 日ごと・トラックID ごとの集計
  const dailyTruckSummaries = useMemo((): DailyTruckSummary[] => {
    const summaryMap = new Map<string, DailyTruckSummary>();

    filteredDriverDailyCosts.forEach((cost) => {
      const key = `${cost.date}-${cost.truckId}`;
      const existing = summaryMap.get(key);

      if (existing) {
        existing.totalCost += cost.cost;
        existing.totalSales += cost.sales;
        existing.totalProfit += (cost.sales - cost.cost);
        existing.totalWorkingHours += cost.workingHours;
      } else {
        summaryMap.set(key, {
          date: cost.date,
          truckId: cost.truckId,
          truckName: cost.truckName,
          truckKlass: cost.truckKlass,
          totalCost: cost.cost,
          totalSales: cost.sales,
          totalProfit: cost.sales - cost.cost,
          totalWorkingHours: cost.workingHours,
        });
      }
    });

    return Array.from(summaryMap.values()).sort((a, b) => {
      const truckCompare = a.truckName.localeCompare(b.truckName);
      if (truckCompare !== 0) return truckCompare;
      return a.date.localeCompare(b.date);
    });
  }, [filteredDriverDailyCosts]);

  // 日ごと・車両クラス ごとの集計
  const dailyKlassSummaries = useMemo((): DailyKlassSummary[] => {
    const summaryMap = new Map<string, DailyKlassSummary>();

    filteredDriverDailyCosts.forEach((cost) => {
      const key = `${cost.date}-${cost.truckKlass}`;
      const existing = summaryMap.get(key);

      if (existing) {
        existing.totalCost += cost.cost;
        existing.totalSales += cost.sales;
        existing.totalProfit += (cost.sales - cost.cost);
        existing.totalWorkingHours += cost.workingHours;

        // トラックIDの重複をチェック
        const truckIds = new Set<number>();
        filteredDriverDailyCosts
          .filter((c) => c.date === cost.date && c.truckKlass === cost.truckKlass)
          .forEach((c) => truckIds.add(c.truckId));
        existing.truckCount = truckIds.size;
      } else {
        summaryMap.set(key, {
          date: cost.date,
          truckKlass: cost.truckKlass,
          totalCost: cost.cost,
          totalSales: cost.sales,
          totalProfit: cost.sales - cost.cost,
          totalWorkingHours: cost.workingHours,
          truckCount: 1,
        });
      }
    });

    return Array.from(summaryMap.values()).sort((a, b) => {
      const klassCompare = a.truckKlass.localeCompare(b.truckKlass);
      if (klassCompare !== 0) return klassCompare;
      return a.date.localeCompare(b.date);
    });
  }, [filteredDriverDailyCosts]);

  // 全体サマリー
  const overallSummary = useMemo(() => {
    const total = filteredDriverDailyCosts.reduce(
      (acc, cost) => ({
        cost: acc.cost + cost.cost,
        sales: acc.sales + cost.sales,
        workingHours: acc.workingHours + cost.workingHours,
      }),
      { cost: 0, sales: 0, workingHours: 0, }
    );

    return {
      ...total,
      profit: total.sales - total.cost,
      truckCount: new Set(filteredDriverDailyCosts.map((c) => c.truckId)).size,
      period: `${datetimeDecorator.toYyyyMmDd(startDate, '/')} - ${datetimeDecorator.toYyyyMmDd(endDate, '/')}`
    };
  }, [filteredDriverDailyCosts, startDate, endDate]);

  const formatCurrency = (amount: number): string => `¥${Math.ceil(amount).toLocaleString()}`;
  const formatHours = (hours: number): string => `${hours.toFixed(1)}h`;

  const getProfitColor = (profit: number): string => {
    if (profit > 0) return '#4caf50';
    if (profit < 0) return '#f44336';
    return '#666';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Typography variant="h4">売上・原価分析</Typography>
        <Typography variant="subtitle2" color="textSecondary">
          {`期間: ${overallSummary.period}`}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* 全体サマリー */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>全体サマリー</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Chip label={`車両数: ${overallSummary.truckCount}台`} />
              <Chip label={`稼働時間: ${formatHours(overallSummary.workingHours)}`} />
              <Chip
                label={`売上: ${formatCurrency(overallSummary.sales)}`}
                color="primary"
              />
              <Chip
                label={`原価: ${formatCurrency(overallSummary.cost)}`}
                color="secondary"
              />
              <Chip
                label={`利益: ${formatCurrency(overallSummary.profit)}`}
                sx={{
                  bgcolor: getProfitColor(overallSummary.profit),
                  color: 'white'
                }}
              />
            </Stack>
          </Paper>

          <Divider />

          {/* タブ */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="車両別集計" />
              <Tab label="車両タイプ別集計" />
            </Tabs>
          </Box>

          {/* 車両別集計タブ */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h5" gutterBottom>日ごと・車両別集計</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>日付</TableCell>
                  <TableCell>車両</TableCell>
                  <TableCell>車両タイプ</TableCell>
                  <TableCell align="right">稼働時間</TableCell>
                  <TableCell align="right">売上</TableCell>
                  <TableCell align="right">原価</TableCell>
                  <TableCell align="right">利益</TableCell>
                  <TableCell align="right">利益率</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyTruckSummaries.map((summary, index) => {
                  const profitRate = summary.totalSales > 0
                    ? ((summary.totalProfit / summary.totalSales) * 100)
                    : 0;

                  return (
                    <TableRow key={`${summary.date}-${summary.truckId}`}>
                      <TableCell>{summary.date}</TableCell>
                      <TableCell>{summary.truckName}</TableCell>
                      <TableCell>{summary.truckKlass}</TableCell>
                      <TableCell align="right">{formatHours(summary.totalWorkingHours)}</TableCell>
                      <TableCell align="right">{formatCurrency(summary.totalSales)}</TableCell>
                      <TableCell align="right">{formatCurrency(summary.totalCost)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: getProfitColor(summary.totalProfit) }}
                      >
                        {formatCurrency(summary.totalProfit)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: getProfitColor(summary.totalProfit) }}
                      >
                        {`${profitRate.toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>

          {/* 車両タイプ別集計タブ */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h5" gutterBottom>日ごと・車両タイプ別集計</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>日付</TableCell>
                  <TableCell>車両タイプ</TableCell>
                  <TableCell align="right">車両数</TableCell>
                  <TableCell align="right">稼働時間</TableCell>
                  <TableCell align="right">売上</TableCell>
                  <TableCell align="right">原価</TableCell>
                  <TableCell align="right">利益</TableCell>
                  <TableCell align="right">利益率</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyKlassSummaries.map((summary, index) => {
                  const profitRate = summary.totalSales > 0
                    ? ((summary.totalProfit / summary.totalSales) * 100)
                    : 0;

                  return (
                    <TableRow key={`${summary.date}-${summary.truckKlass}`}>
                      <TableCell>{summary.date}</TableCell>
                      <TableCell>{summary.truckKlass}</TableCell>
                      <TableCell align="right">{summary.truckCount}</TableCell>
                      <TableCell align="right">{formatHours(summary.totalWorkingHours)}</TableCell>
                      <TableCell align="right">{formatCurrency(summary.totalSales)}</TableCell>
                      <TableCell align="right">{formatCurrency(summary.totalCost)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: getProfitColor(summary.totalProfit) }}
                      >
                        {formatCurrency(summary.totalProfit)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: getProfitColor(summary.totalProfit) }}
                      >
                        {`${profitRate.toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TabPanel>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default CostAnalysisDialog;
